/**
 * "Reconcile from list" — paste a city / water-authority device list against
 * the site's Devices section. Flow: paste → server-side match preview →
 * per-row action (Update / Create new / Leave unmatched) → Apply.
 *
 * Nothing writes until Apply. Matching happens on the server
 * (services/authorityListReconcile.ts) so this preview and the apply can
 * never disagree. Existing devices missing from the list are NEVER deleted —
 * they get flagged "Not on authority list — verify on site" (checkbox,
 * default on), or — when they look like a stale ST duplicate of a list row's
 * device — the office can fold them into the survivor with "Merge into it".
 *
 * Launched from the site Location Hub AND from the job page Results card
 * (pass `jobId`): the reconcile always edits SITE records; the job launch
 * additionally offers "Sync this job's device list to the reconciled site
 * list" on Apply.
 */
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { AlertTriangle, ArrowRight, Link2 } from "lucide-react";

interface ParsedRow {
    line: number;
    serial: string | null;
    sizeLabel: string | null;
    make: string | null;
    model: string | null;
    typeRaw: string | null;
    assemblyType: string | null;
    location: string | null;
    system: string | null;
    serviceType: string | null;
    serviceTypeOther: string | null;
}

interface FieldChange {
    field: string;
    from: string | null;
    to: string | null;
}

interface PlanRow {
    index: number;
    parsed: ParsedRow;
    action: "update" | "create" | "skip";
    matchedAssetId: string | null;
    matchedAssetName: string | null;
    matchedBy: "serial" | "size_make_model" | null;
    changes: FieldChange[];
    serialCaptureNeeded: boolean;
    suggestedParentIndex: number | null;
}

interface Leftover {
    asset: {
        id: string;
        name: string | null;
        serial_number: string | null;
        size_label: string | null;
        manufacturer: string | null;
        model: string | null;
        needs_verification_reason: string | null;
    };
    flag: boolean;
    possibleDuplicateOfIndex: number | null;
}

interface ParseFailure {
    line: number;
    raw: string;
    reason: string;
}

interface PreviewResponse {
    rows: PlanRow[];
    leftovers: Leftover[];
    parseErrors: string[];
    parseFailures?: ParseFailure[];
}

const FIELD_LABEL: Record<string, string> = {
    serial_number: "Serial",
    size_label: "Size",
    manufacturer: "Make",
    model: "Model",
    assembly_type: "Type",
    location_description: "Location",
    service_type: "Serves",
    service_type_other: "Serves (other)",
};

const DETECTOR_TYPES = new Set(["dcda", "rpda", "dcda_ii", "rpda_ii"]);
const BYPASS_LEG_TYPES = new Set(["dc", "rpz"]);

function rowLabel(r: PlanRow): string {
    const identity = [r.parsed.sizeLabel, r.parsed.make, r.parsed.model].filter(Boolean).join(" ");
    return r.parsed.serial ? `${identity} S/N ${r.parsed.serial}` : identity || `row ${r.index + 1}`;
}

export function ReconcileFromListDialog({
    siteId,
    jobId,
    open,
    onClose,
}: {
    siteId: string;
    /** When launched from a job page: offer syncing the job's device list after Apply. */
    jobId?: string | null;
    open: boolean;
    onClose: () => void;
}) {
    const qc = useQueryClient();
    const [text, setText] = useState("");
    const [preview, setPreview] = useState<PreviewResponse | null>(null);
    const [actionOverrides, setActionOverrides] = useState<Record<number, PlanRow["action"]>>({});
    const [flagOverrides, setFlagOverrides] = useState<Record<string, boolean>>({});
    /** Leftover asset id → row index it merges into ("Merge into it" choice). */
    const [mergeChoices, setMergeChoices] = useState<Record<string, number | null>>({});
    /** Row index → confirmed bypass-parent row index (null = explicitly no link). */
    const [parentOverrides, setParentOverrides] = useState<Record<number, number | null>>({});
    const [syncJob, setSyncJob] = useState(true);
    /** Prompt 20a: explicit acknowledgement before applying past unparsed lines. */
    const [skipUnparsed, setSkipUnparsed] = useState(false);
    /** Pasted line number → in-progress inline edit of a failed line. */
    const [lineEdits, setLineEdits] = useState<Record<number, string>>({});

    const reset = () => {
        setPreview(null);
        setActionOverrides({});
        setFlagOverrides({});
        setMergeChoices({});
        setParentOverrides({});
        setSkipUnparsed(false);
        setLineEdits({});
    };

    const previewMutation = useMutation({
        mutationFn: (t: string) =>
            api.post<PreviewResponse>(`/api/sites/${siteId}/assets/reconcile-preview`, { text: t }),
        onSuccess: (data) => {
            setPreview(data);
            setActionOverrides({});
            setFlagOverrides({});
            setMergeChoices({});
            setParentOverrides({});
            setSkipUnparsed(false);
            setLineEdits({});
        },
        onError: (e: Error) => toast.error(e.message || "Could not parse the pasted list"),
    });

    /** Fix one failed line in place and re-run the whole preview. */
    const reparseLine = (lineNo: number, fixed: string) => {
        if (!fixed.trim()) return;
        const lines = text.split(/\r?\n/);
        lines[lineNo - 1] = fixed;
        const next = lines.join("\n");
        setText(next);
        previewMutation.mutate(next);
    };

    const effectiveAction = (row: PlanRow): PlanRow["action"] => actionOverrides[row.index] ?? row.action;
    const flagChecked = (l: Leftover) => flagOverrides[l.asset.id] ?? l.flag;
    const mergeTarget = (l: Leftover): number | null => {
        const chosen = mergeChoices[l.asset.id];
        return chosen === undefined ? null : chosen;
    };
    const effectiveParent = (row: PlanRow): number | null => {
        const o = parentOverrides[row.index];
        return o === undefined ? row.suggestedParentIndex : o;
    };

    /** DCDA/RPDA plan rows — the "Link to parent" candidates. */
    const parentCandidates = useMemo(
        () =>
            (preview?.rows ?? []).filter(
                (r) =>
                    r.parsed.assemblyType && DETECTOR_TYPES.has(r.parsed.assemblyType) && effectiveAction(r) !== "skip",
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [preview, actionOverrides],
    );

    const syncJobMutation = useMutation({
        mutationFn: () =>
            api.post<{ added: number; removed: number; keptTested: number }>(
                `/api/jobs/${jobId}/assets/sync-to-site-list`,
                {},
            ),
        onSuccess: (r) => {
            qc.invalidateQueries({ queryKey: ["job", jobId] });
            toast.success(
                `Job device list synced: ${r.added} added` +
                    (r.removed ? `, ${r.removed} removed` : "") +
                    (r.keptTested
                        ? `. ${r.keptTested} flagged device${r.keptTested === 1 ? "" : "s"} stay on the job for the tech to check.`
                        : ""),
            );
        },
        onError: (e: Error) => toast.error(e.message || "Job sync failed — the site records were still reconciled"),
    });

    const applyMutation = useMutation({
        mutationFn: () => {
            const rows = (preview?.rows ?? []).map((r) => ({
                action: effectiveAction(r),
                assetId: effectiveAction(r) === "update" ? r.matchedAssetId : null,
                parsed: r.parsed,
                linkParentRowIndex: effectiveAction(r) === "skip" ? null : effectiveParent(r),
            }));
            const merges = (preview?.leftovers ?? [])
                .map((l) => ({ assetId: l.asset.id, intoRowIndex: mergeTarget(l) }))
                .filter((m): m is { assetId: string; intoRowIndex: number } => m.intoRowIndex != null);
            const mergedIds = new Set(merges.map((m) => m.assetId));
            const flagAssetIds = (preview?.leftovers ?? [])
                .filter((l) => !mergedIds.has(l.asset.id) && flagChecked(l))
                .map((l) => l.asset.id);
            return api.post<{
                result: {
                    updated: number;
                    created: number;
                    flagged: number;
                    unchanged: number;
                    merged: number;
                    linked: number;
                };
            }>(`/api/sites/${siteId}/assets/reconcile-apply`, {
                rows,
                flagAssetIds,
                merges,
                source: "Authority list paste",
                skippedUnparsedLines: preview?.parseFailures?.length ?? 0,
            });
        },
        onSuccess: ({ result }) => {
            qc.invalidateQueries({ queryKey: ["site-trade-assets", siteId] });
            qc.invalidateQueries({ queryKey: ["site-trades", siteId] });
            qc.invalidateQueries({ queryKey: ["site-assets", siteId] });
            const failed = preview?.parseFailures?.length ?? 0;
            toast.success(
                `Reconciled: ${result.updated} updated, ${result.created} created, ${result.flagged} flagged for on-site verification` +
                    (result.merged ? `, ${result.merged} duplicate${result.merged === 1 ? "" : "s"} merged` : "") +
                    (result.linked ? `, ${result.linked} bypass link${result.linked === 1 ? "" : "s"}` : "") +
                    (result.unchanged ? `, ${result.unchanged} already in sync` : "") +
                    (failed ? ` — ${failed} unparsed line${failed === 1 ? "" : "s"} skipped` : ""),
            );
            if (jobId && syncJob) syncJobMutation.mutate();
            reset();
            setText("");
            onClose();
        },
        onError: (e: Error) => toast.error(e.message || "Apply failed — nothing was written"),
    });

    const counts = useMemo(() => {
        if (!preview) return null;
        const c = { update: 0, create: 0, skip: 0 };
        for (const r of preview.rows) c[effectiveAction(r)] += 1;
        const merged = preview.leftovers.filter((l) => mergeTarget(l) != null).length;
        const flag = preview.leftovers.filter((l) => mergeTarget(l) == null && flagChecked(l)).length;
        return { ...c, flag, merged };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preview, actionOverrides, flagOverrides, mergeChoices]);

    return (
        <Dialog open={open} onClose={onClose} title="Reconcile from list" className="max-w-4xl">
            {!preview ? (
                <div className="space-y-3">
                    <p className="text-sm text-foreground/80">
                        Paste the city / authority device list — one device per line, columns separated by tabs
                        (spreadsheet paste) or pipes:{" "}
                        <code className="text-xs">serial | size | make | model (type) | location | system</code>.
                        Nothing is written until you press Apply.
                    </p>
                    <Textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder={'MK5783 | 6" | Ames | Colt 200 (DC) | Fire Riser Room | Fire Protection'}
                        rows={12}
                        className="font-mono text-xs"
                    />
                    <div className="flex justify-end gap-2">
                        <Button variant="ghost" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            disabled={!text.trim() || previewMutation.isPending}
                            onClick={() => previewMutation.mutate(text)}
                        >
                            {previewMutation.isPending ? "Matching…" : "Preview matches"}
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="space-y-5">
                    {(preview.parseFailures ?? []).length > 0 && (
                        <div className="rounded-md border border-red-500/50 bg-red-500/10 p-3 text-xs space-y-2">
                            <div className="font-medium flex items-center gap-1.5 text-red-700 dark:text-red-400">
                                <AlertTriangle className="h-3.5 w-3.5" /> {preview.parseFailures!.length} line
                                {preview.parseFailures!.length === 1 ? "" : "s"} could not be parsed. Fix them below or
                                explicitly skip them — nothing is silently dropped.
                            </div>
                            {preview.parseFailures!.map((f) => (
                                <div key={f.line} className="space-y-1">
                                    <div className="font-mono text-red-700 dark:text-red-400 break-all">
                                        Line {f.line}: {f.raw}
                                    </div>
                                    <div className="text-foreground/70">{f.reason}</div>
                                    <div className="flex items-center gap-1.5">
                                        <input
                                            className="h-6 flex-1 rounded-md border border-input bg-background px-1.5 font-mono text-[11px]"
                                            value={lineEdits[f.line] ?? f.raw}
                                            onChange={(e) =>
                                                setLineEdits((prev) => ({ ...prev, [f.line]: e.target.value }))
                                            }
                                            aria-label={`Edit line ${f.line}`}
                                        />
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-6 px-2 text-[11px]"
                                            disabled={previewMutation.isPending || !(lineEdits[f.line] ?? f.raw).trim()}
                                            onClick={() => reparseLine(f.line, lineEdits[f.line] ?? f.raw)}
                                        >
                                            Re-parse
                                        </Button>
                                    </div>
                                </div>
                            ))}
                            <label className="flex items-center gap-2 pt-1 border-t border-red-500/30 text-foreground/80">
                                <input
                                    type="checkbox"
                                    checked={skipUnparsed}
                                    onChange={(e) => setSkipUnparsed(e.target.checked)}
                                />
                                Skip {preview.parseFailures!.length} unparsed line
                                {preview.parseFailures!.length === 1 ? "" : "s"} — apply the rest anyway
                            </label>
                        </div>
                    )}

                    <div>
                        <h3 className="text-sm font-semibold mb-2">
                            Match preview{" "}
                            <span className="font-normal text-foreground/70">
                                ({preview.rows.length + (preview.parseFailures?.length ?? 0)} row
                                {preview.rows.length + (preview.parseFailures?.length ?? 0) === 1 ? "" : "s"} pasted →{" "}
                                {preview.rows.filter((r) => r.matchedAssetId).length} matched,{" "}
                                {preview.rows.filter((r) => !r.matchedAssetId).length} new
                                {(preview.parseFailures?.length ?? 0) > 0
                                    ? `, ${preview.parseFailures!.length} could not be parsed`
                                    : ""}
                                )
                            </span>
                        </h3>
                        <div className="rounded-md border overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="border-b bg-secondary/50 text-left">
                                        <th className="px-2 py-1.5 font-medium">List row</th>
                                        <th className="px-2 py-1.5 font-medium">Matched device</th>
                                        <th className="px-2 py-1.5 font-medium">Changes</th>
                                        <th className="px-2 py-1.5 font-medium">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {preview.rows.map((r) => {
                                        const action = effectiveAction(r);
                                        const isBypassLeg =
                                            r.parsed.assemblyType != null &&
                                            BYPASS_LEG_TYPES.has(r.parsed.assemblyType);
                                        const linkTarget = effectiveParent(r);
                                        return (
                                            <tr key={r.index} className="border-b last:border-b-0 align-top">
                                                <td className="px-2 py-1.5">
                                                    <div className="font-medium">
                                                        {[r.parsed.sizeLabel, r.parsed.make, r.parsed.model]
                                                            .filter(Boolean)
                                                            .join(" ")}
                                                        {r.parsed.typeRaw ? ` (${r.parsed.typeRaw})` : ""}
                                                    </div>
                                                    <div className="text-foreground/70 font-mono">
                                                        {r.parsed.serial ?? "no serial"}
                                                    </div>
                                                    {r.parsed.location && (
                                                        <div className="text-foreground/70">{r.parsed.location}</div>
                                                    )}
                                                </td>
                                                <td className="px-2 py-1.5">
                                                    {r.matchedAssetId ? (
                                                        <div>
                                                            <div>{r.matchedAssetName ?? r.matchedAssetId}</div>
                                                            {r.serialCaptureNeeded ? (
                                                                <Badge
                                                                    variant="outline"
                                                                    className="mt-0.5 text-[10px] border-amber-500/40 text-amber-600 bg-amber-500/10"
                                                                    title="The authority list has no usable serial for this row — the match is size + make + model only. The tech must capture the serial off the device tag on site."
                                                                >
                                                                    model match — no serial
                                                                </Badge>
                                                            ) : (
                                                                <Badge
                                                                    variant="outline"
                                                                    className="mt-0.5 text-[10px]"
                                                                    title={
                                                                        r.matchedBy === "serial"
                                                                            ? "Matched by serial number"
                                                                            : "Matched by size + make + model family (identical devices assigned one-to-one)"
                                                                    }
                                                                >
                                                                    {r.matchedBy === "serial"
                                                                        ? "serial match"
                                                                        : "size/make/model match"}
                                                                </Badge>
                                                            )}
                                                            {r.serialCaptureNeeded && (
                                                                <div className="mt-0.5 text-[10px] text-amber-700 dark:text-amber-400">
                                                                    Tech must capture the serial on site.
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="text-foreground/60">
                                                            No match — new device
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-2 py-1.5">
                                                    {action === "update" && r.changes.length === 0 && (
                                                        <span className="text-foreground/60">Already in sync</span>
                                                    )}
                                                    {action === "update" &&
                                                        r.changes.map((c) => (
                                                            <div
                                                                key={c.field}
                                                                className="flex items-center gap-1 flex-wrap"
                                                            >
                                                                <span className="text-foreground/70">
                                                                    {FIELD_LABEL[c.field] ?? c.field}:
                                                                </span>
                                                                <span
                                                                    className={cn(
                                                                        c.from && "line-through text-muted-foreground",
                                                                    )}
                                                                >
                                                                    {c.from ?? "—"}
                                                                </span>
                                                                <ArrowRight className="h-3 w-3 text-foreground/50" />
                                                                <span className="font-medium">{c.to ?? "—"}</span>
                                                            </div>
                                                        ))}
                                                    {action === "create" && (
                                                        <span className="text-emerald-600">
                                                            Will create a new device
                                                        </span>
                                                    )}
                                                    {action === "skip" && (
                                                        <span className="text-foreground/60">No changes</span>
                                                    )}
                                                    {isBypassLeg &&
                                                        parentCandidates.length > 0 &&
                                                        action !== "skip" && (
                                                            <div className="mt-1 flex items-center gap-1 flex-wrap">
                                                                <Link2 className="h-3 w-3 text-foreground/50" />
                                                                <span className="text-foreground/70">Bypass of:</span>
                                                                <select
                                                                    className="h-6 rounded-md border border-input bg-background px-1 text-[11px]"
                                                                    value={linkTarget ?? ""}
                                                                    onChange={(e) =>
                                                                        setParentOverrides((prev) => ({
                                                                            ...prev,
                                                                            [r.index]:
                                                                                e.target.value === ""
                                                                                    ? null
                                                                                    : Number(e.target.value),
                                                                        }))
                                                                    }
                                                                >
                                                                    <option value="">— not a bypass —</option>
                                                                    {parentCandidates
                                                                        .filter((p) => p.index !== r.index)
                                                                        .map((p) => (
                                                                            <option key={p.index} value={p.index}>
                                                                                {rowLabel(p)}
                                                                            </option>
                                                                        ))}
                                                                </select>
                                                                {r.suggestedParentIndex != null &&
                                                                    linkTarget === r.suggestedParentIndex && (
                                                                        <Badge
                                                                            variant="outline"
                                                                            className="text-[9px] border-sky-500/40 text-sky-600 bg-sky-500/10"
                                                                            title="Suggested because the rows share a location or the location says “bypass”. Confirm or clear before Apply."
                                                                        >
                                                                            suggested
                                                                        </Badge>
                                                                    )}
                                                            </div>
                                                        )}
                                                </td>
                                                <td className="px-2 py-1.5">
                                                    <select
                                                        className="h-7 rounded-md border border-input bg-background px-1.5 text-xs"
                                                        value={action}
                                                        onChange={(e) =>
                                                            setActionOverrides((prev) => ({
                                                                ...prev,
                                                                [r.index]: e.target.value as PlanRow["action"],
                                                            }))
                                                        }
                                                    >
                                                        {r.matchedAssetId && <option value="update">Update</option>}
                                                        <option value="create">Create new</option>
                                                        <option value="skip">Leave unmatched</option>
                                                    </select>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {preview.leftovers.length > 0 && (
                        <div>
                            <h3 className="text-sm font-semibold mb-1">Existing devices not on the list</h3>
                            <p className="text-xs text-foreground/70 mb-2">
                                These are never deleted. Checked devices get flagged{" "}
                                <span className="font-medium">“Not on authority list — verify on site”</span>. Devices
                                that look like a stale duplicate of a list row can be merged into it instead.
                            </p>
                            <div className="space-y-1.5">
                                {preview.leftovers.map((l) => {
                                    const dupRow =
                                        l.possibleDuplicateOfIndex != null
                                            ? (preview.rows.find((r) => r.index === l.possibleDuplicateOfIndex) ?? null)
                                            : null;
                                    const merging = mergeTarget(l) != null;
                                    return (
                                        <div key={l.asset.id} className="rounded border px-2 py-1.5 space-y-1">
                                            <div className="flex items-center gap-2 text-xs flex-wrap">
                                                <span className="font-medium">{l.asset.name ?? "Unnamed device"}</span>
                                                <span className="text-foreground/70 font-mono">
                                                    {l.asset.serial_number
                                                        ? `S/N ${l.asset.serial_number}`
                                                        : "no serial"}
                                                </span>
                                                <span className="text-foreground/70">
                                                    {[l.asset.size_label, l.asset.manufacturer, l.asset.model]
                                                        .filter(Boolean)
                                                        .join(" ")}
                                                </span>
                                            </div>
                                            {dupRow && (
                                                <div className="flex items-center gap-2 text-[11px] flex-wrap">
                                                    <Badge
                                                        variant="outline"
                                                        className="text-[10px] border-amber-500/40 text-amber-600 bg-amber-500/10"
                                                    >
                                                        Possible duplicate of {rowLabel(dupRow)}
                                                    </Badge>
                                                    <label className="inline-flex items-center gap-1">
                                                        <input
                                                            type="radio"
                                                            name={`dup-${l.asset.id}`}
                                                            checked={merging}
                                                            onChange={() =>
                                                                setMergeChoices((prev) => ({
                                                                    ...prev,
                                                                    [l.asset.id]: l.possibleDuplicateOfIndex,
                                                                }))
                                                            }
                                                        />
                                                        Merge into it
                                                    </label>
                                                    <label className="inline-flex items-center gap-1">
                                                        <input
                                                            type="radio"
                                                            name={`dup-${l.asset.id}`}
                                                            checked={!merging}
                                                            onChange={() =>
                                                                setMergeChoices((prev) => ({
                                                                    ...prev,
                                                                    [l.asset.id]: null,
                                                                }))
                                                            }
                                                        />
                                                        Keep separate + flag
                                                    </label>
                                                </div>
                                            )}
                                            {!merging && (
                                                <label className="flex items-center gap-2 text-[11px] text-foreground/80">
                                                    <input
                                                        type="checkbox"
                                                        checked={flagChecked(l)}
                                                        onChange={(e) =>
                                                            setFlagOverrides((prev) => ({
                                                                ...prev,
                                                                [l.asset.id]: e.target.checked,
                                                            }))
                                                        }
                                                    />
                                                    Flag “Not on authority list — verify on site”
                                                </label>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {jobId && (
                        <label className="flex items-center gap-2 text-xs rounded-md border bg-secondary/30 px-3 py-2">
                            <input type="checkbox" checked={syncJob} onChange={(e) => setSyncJob(e.target.checked)} />
                            <span>
                                Sync this job’s device list to the reconciled site list — adds missing devices, removes
                                flagged not-on-list devices. Anything already tested on this job stays.
                            </span>
                        </label>
                    )}

                    <div className="flex items-center justify-between gap-2 pt-1 border-t">
                        <Button variant="ghost" size="sm" onClick={reset}>
                            ← Back to paste
                        </Button>
                        <div className="flex items-center gap-3">
                            {counts && (
                                <span className="text-xs text-foreground/70 tabular-nums">
                                    {counts.update} update · {counts.create} create · {counts.skip} skipped ·{" "}
                                    {counts.flag} flagged
                                    {counts.merged ? ` · ${counts.merged} merged` : ""}
                                    {(preview.parseFailures?.length ?? 0) > 0
                                        ? ` · ${preview.parseFailures!.length} unparsed`
                                        : ""}
                                </span>
                            )}
                            <Button
                                disabled={
                                    applyMutation.isPending ||
                                    ((preview.parseFailures?.length ?? 0) > 0 && !skipUnparsed)
                                }
                                title={
                                    (preview.parseFailures?.length ?? 0) > 0 && !skipUnparsed
                                        ? "Some pasted lines could not be parsed — fix them or check “skip unparsed lines” first."
                                        : undefined
                                }
                                onClick={() => applyMutation.mutate()}
                            >
                                {applyMutation.isPending ? "Applying…" : "Apply"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </Dialog>
    );
}
